import { Vector3 } from 'three';

import { convexHull, type Polygon2D } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/geometry2d';
import {
    getPartViewProfile,
    getPlacedPrefabs,
    type ProfileSource
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/prefabViewProfile';
import {
    emptyRect,
    extendRect,
    isRectValid,
    rectOfPoints,
    toDepth,
    toView,
    type Rect,
    type Vec2,
    type ViewFrame
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';
import {
    removeDuplicates,
    sortVerticesInPlane
} from 'app/modules/wallforming/components/scenes/objects/dimensions/utils/dimensionUtils';
import { EPSILON } from 'shared/constants';

import type { PrefabModel } from 'api/models';
import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';
import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';

const MARKER_SIZE = 0.05;
const UNIVERSAL_ELEMENT_TYPE = 2;

export type SceneItemKind = 'anchor' | 'clamp' | 'accessory';

export interface DimensionElementTypes {
    panels: ReadonlySet<number>;
    connectors: ReadonlySet<number>;
    anchors: ReadonlySet<number>;
}

export interface ScenePanel {
    id: string;
    groupId: string | null;
    rect: Rect;
    isUniversal?: boolean;
}

export interface SceneAccessory {
    id: string;
    kind: SceneItemKind;
    groupId: string | null;
    polygons: Polygon2D[];
    bounds: Rect;
    /** reference point used for dimensioning (centre of the silhouette) */
    center: Vec2;
    depth: number;
    source: ProfileSource | 'marker';
    isUniversal?: boolean;
}

/** Everything that is dimensioned on one wall POI, expressed in view coordinates (metres). */
export interface DimensionScene {
    wallOutline: Vec2[];
    wallBounds: Rect | null;
    panels: ScenePanel[];
    accessories: SceneAccessory[];
    /** view y of the floor (lowest building point) */
    floorY: number;
    /** largest depth of the panel cover areas (towards the viewer) */
    frontDepth: number;
}

export interface DimensionSceneInput {
    elementTypes: DimensionElementTypes;
    frame: ViewFrame;
    poiGuid: string;
    poiArticles: Record<string, string[]>;
    articles: Record<string, Article>;
    representations: ReadonlyMap<string, PrefabModel>;
    articleColliders: ColliderPoint[];
    buildingPoints: Vector3[];
    instanceToGroupMap: Record<string, string>;
    isExcluded: (instanceId: string) => boolean;
    /** connector shapes and labels */
    includeAccessories: boolean;
    /** anchor shapes, labels and spacing chain */
    includeAnchors: boolean;
}

type ArticleKind = 'panel' | SceneItemKind;

function classifyArticle(article: Article, elementTypes: DimensionElementTypes): ArticleKind | null {
    if (article.elementType === undefined) {
        return null;
    }
    if (elementTypes.anchors.has(article.elementType)) {
        return 'anchor';
    }
    if (elementTypes.connectors.has(article.elementType)) {
        return 'clamp';
    }
    if (elementTypes.panels.has(article.elementType)) {
        return 'panel';
    }
    return null;
}

/** Article ids on the POI whose silhouette has to be derived from the full prefab geometry. */
export function getAccessoryArticleIds(
    poiArticles: Record<string, string[]>,
    articles: Record<string, Article>,
    includeAccessories: boolean,
    includeAnchors: boolean,
    elementTypes: DimensionElementTypes
): string[] {
    return Object.keys(poiArticles)
        .filter((articleId) => {
            const article = articles[articleId];
            if (!article) {
                return false;
            }
            const kind = classifyArticle(article, elementTypes);
            if (!kind || kind === 'panel') {
                return false;
            }
            return kind === 'anchor' ? includeAnchors : includeAccessories;
        })
        .sort();
}

/** Keeps only collider points of the instance part(s) that face the wall best (e.g. one side of a corner panel). */
function filterBestFacingParts(colliders: ColliderPoint[], normal: Vector3): ColliderPoint[] {
    const bestScoreByRelation = new Map<string, number>();
    const scoreByPart = new Map<string, number>();

    for (const collider of colliders) {
        if (!collider.name || !collider.alignment) {
            continue;
        }
        const partKey = `${collider.relationId}\0${collider.name}`;
        const score = Math.abs(collider.alignment.dot(normal));
        scoreByPart.set(partKey, Math.max(scoreByPart.get(partKey) ?? 0, score));
        bestScoreByRelation.set(collider.relationId, Math.max(bestScoreByRelation.get(collider.relationId) ?? 0, score));
    }

    return colliders.filter((collider) => {
        if (!collider.name || !collider.alignment) {
            return true;
        }
        const score = scoreByPart.get(`${collider.relationId}\0${collider.name}`) ?? 0;
        return score >= (bestScoreByRelation.get(collider.relationId) ?? 0) - EPSILON;
    });
}

function buildPanels(
    input: DimensionSceneInput,
    panelInstanceIds: Set<string>,
    universalPanelInstanceIds: Set<string>
): { panels: ScenePanel[]; frontDepth: number } {
    const { frame, poiGuid, articleColliders, instanceToGroupMap, isExcluded } = input;
    const relevant = articleColliders.filter(
        (c) => panelInstanceIds.has(c.relationId) && c.poiGuids.includes(poiGuid) && !isExcluded(c.relationId)
    );

    const pointsByRelation = new Map<string, Vec2[]>();
    let frontDepth = 0;
    for (const collider of filterBestFacingParts(relevant, frame.n)) {
        const points = pointsByRelation.get(collider.relationId) ?? [];
        points.push(toView(frame, collider.position));
        pointsByRelation.set(collider.relationId, points);
        frontDepth = Math.max(frontDepth, toDepth(frame, collider.position));
    }

    const panels: ScenePanel[] = [];
    for (const [id, points] of pointsByRelation) {
        const rect = rectOfPoints(points);
        if (points.length < 3 || rect.maxX - rect.minX < EPSILON || rect.maxY - rect.minY < EPSILON) {
            continue;
        }
        panels.push({
            id,
            groupId: instanceToGroupMap[id] ?? null,
            rect,
            isUniversal: universalPanelInstanceIds.has(id)
        });
    }
    panels.sort((a, b) => a.rect.minX - b.rect.minX || a.rect.minY - b.rect.minY);
    return { panels, frontDepth };
}

function buildAccessory(
    input: DimensionSceneInput,
    instanceId: string,
    kind: SceneItemKind,
    representation: PrefabModel | undefined,
    article: Article
): SceneAccessory | null {
    const { frame, instanceToGroupMap } = input;
    const instance = article.articleInstances[instanceId];
    const firstPart = instance ? Object.values(instance.parts)[0] : undefined;
    if (!instance || !firstPart) {
        return null;
    }

    const polygons: Polygon2D[] = [];
    let source: SceneAccessory['source'] = 'marker';
    if (representation) {
        for (const { prefab, part } of getPlacedPrefabs(representation, instance.parts)) {
            const profile = getPartViewProfile(prefab, part, frame);
            if (profile) {
                polygons.push(...profile.polygons);
                source = profile.source;
            }
        }
    }

    if (polygons.length === 0) {
        // geometry not (yet) available: square marker around the insertion point
        const c = toView(frame, firstPart.position);
        const s = MARKER_SIZE / 2;
        polygons.push([
            [
                { x: c.x - s, y: c.y - s },
                { x: c.x + s, y: c.y - s },
                { x: c.x + s, y: c.y + s },
                { x: c.x - s, y: c.y + s }
            ]
        ]);
    }

    const bounds = emptyRect();
    for (const polygon of polygons) {
        for (const p of polygon[0] ?? []) {
            extendRect(bounds, p);
        }
    }

    return {
        id: instanceId,
        kind,
        groupId: instanceToGroupMap[instanceId] ?? null,
        polygons,
        bounds,
        center: { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 },
        depth: toDepth(frame, firstPart.position),
        source,
        isUniversal: article.elementType === UNIVERSAL_ELEMENT_TYPE
    };
}

function buildWallOutline(frame: ViewFrame, buildingPoints: Vector3[]): Vec2[] {
    const unique = removeDuplicates(buildingPoints.map((p) => p.clone()));
    if (unique.length < 3) {
        return [];
    }
    const sorted = sortVerticesInPlane(unique, frame.n).map((p) => toView(frame, p));
    return sorted.length >= 3 ? sorted : convexHull(unique.map((p) => toView(frame, p)));
}

export function buildDimensionScene(input: DimensionSceneInput): DimensionScene {
    const { poiArticles, articles, representations, isExcluded, includeAccessories, includeAnchors, frame, buildingPoints } =
        input;

    const panelInstanceIds = new Set<string>();
    const universalPanelInstanceIds = new Set<string>();
    const accessories: SceneAccessory[] = [];

    for (const [articleId, instanceIds] of Object.entries(poiArticles)) {
        const article = articles[articleId];
        if (!article) {
            continue;
        }

        const kind = classifyArticle(article, input.elementTypes);
        if (!kind) {
            continue;
        }
        if (kind === 'panel') {
            instanceIds.forEach((id) => {
                panelInstanceIds.add(id);
                if (article.elementType === UNIVERSAL_ELEMENT_TYPE) {
                    universalPanelInstanceIds.add(id);
                }
            });
            continue;
        }
        if (kind === 'anchor' ? !includeAnchors : !includeAccessories) {
            continue;
        }

        for (const instanceId of instanceIds) {
            if (isExcluded(instanceId)) {
                continue;
            }
            const accessory = buildAccessory(input, instanceId, kind, representations.get(articleId), article);
            if (accessory) {
                accessories.push(accessory);
            }
        }
    }

    // back to front, so nearer accessories are drawn on top
    accessories.sort((a, b) => a.depth - b.depth);

    const { panels, frontDepth } = buildPanels(input, panelInstanceIds, universalPanelInstanceIds);
    const wallOutline = buildWallOutline(frame, buildingPoints);
    const wallBounds = wallOutline.length >= 3 ? rectOfPoints(wallOutline) : null;

    let floorY = wallBounds?.minY ?? Infinity;
    if (!Number.isFinite(floorY)) {
        floorY = panels.reduce((min, p) => Math.min(min, p.rect.minY), 0);
    }

    return {
        wallOutline,
        wallBounds: wallBounds && isRectValid(wallBounds) ? wallBounds : null,
        panels,
        accessories,
        floorY,
        frontDepth
    };
}
